import React from 'react'
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, FormControl, InputLabel, Select, MenuItem, Grid, Link as MuiLink } from '@mui/material'
import { router, usePage, Link } from '@inertiajs/react'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import StatusBadge from '../../../../components/ui/StatusBadge'
import FormattedCurrency from '../../../../components/ui/FormattedCurrency'

Show.layout = (page) => <AppShell>{page}</AppShell>

export default function Show({ property, fiscal_year, fiscal_years = [], account, lines = [] }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}`

  const onChangeFiscalYear = (id) => {
    router.visit(`${basePath}/ledger_accounts/${account.id}?fiscal_year_id=${id}`)
  }

  // Compute running balance, signed by normal_balance.
  let running = parseFloat(account.opening_balance || 0)
  const isDebit = account.normal_balance === 'debit'
  const linesWithRunning = lines.map((line) => {
    const debit = parseFloat(line.debit_amount || 0)
    const credit = parseFloat(line.credit_amount || 0)
    running += isDebit ? (debit - credit) : (credit - debit)
    return { ...line, running_balance: running }
  })

  return (
    <Box>
      <PageHeader
        title={`Konto ${account.number} — ${account.name}`}
        breadcrumbs={[
          { label: property.name, href: basePath },
          { label: 'Saldenliste', href: `${basePath}/balance_sheet?fiscal_year_id=${fiscal_year.id}` },
          { label: account.number },
        ]}
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Geschäftsjahr</InputLabel>
          <Select
            value={fiscal_year.id}
            label="Geschäftsjahr"
            onChange={(e) => onChangeFiscalYear(e.target.value)}
            data-testid="fiscal-year-filter"
          >
            {fiscal_years.map((fy) => (
              <MenuItem key={fy.id} value={fy.id}>{fy.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Eröffnungsbilanz</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }} data-testid="summary-opening">
              <FormattedCurrency value={account.opening_balance} />
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Soll</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }} data-testid="summary-debit">
              <FormattedCurrency value={account.period_debit} />
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Haben</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }} data-testid="summary-credit">
              <FormattedCurrency value={account.period_credit} />
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Saldo</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }} data-testid="summary-closing">
              <FormattedCurrency value={account.closing_balance} />
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Buchungen</Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
              <TableCell sx={{ fontWeight: 600 }}>Datum</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Buchungsnr.</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Soll</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Haben</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Saldo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {linesWithRunning.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Keine Buchungen vorhanden.</Typography>
                </TableCell>
              </TableRow>
            )}
            {linesWithRunning.map((line) => (
              <TableRow key={line.id} hover>
                <TableCell>{line.entry_date}</TableCell>
                <TableCell>
                  <MuiLink component={Link} href={`${basePath}/journal/${line.journal_entry_id}`} underline="hover">
                    {line.entry_number || '–'}
                  </MuiLink>
                </TableCell>
                <TableCell>{line.line_description || line.description}</TableCell>
                <TableCell><StatusBadge type="journal_entry_status" value={line.status} /></TableCell>
                <TableCell align="right">{parseFloat(line.debit_amount) ? <FormattedCurrency value={line.debit_amount} /> : ''}</TableCell>
                <TableCell align="right">{parseFloat(line.credit_amount) ? <FormattedCurrency value={line.credit_amount} /> : ''}</TableCell>
                <TableCell align="right"><FormattedCurrency value={line.running_balance} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
